import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, computed, signal } from '@angular/core';

/**
 * Centralizes layout helpers such as the INSPINIA sidebar collapse behaviour so
 * components can react consistently without relying on jQuery helpers.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
    private static readonly COLLAPSE_STORAGE_KEY = 'collapse_menu';

    private readonly _isSidebarCollapsed = signal(this.readInitialState());

    /** Exposes the current collapse state as a computed signal for templates. */
    readonly isSidebarCollapsed = computed(() => this._isSidebarCollapsed());

    constructor(@Inject(DOCUMENT) private readonly document: Document) {
        // Ensure the body class reflects any persisted state on service creation.
        this.applyBodyClass(this._isSidebarCollapsed());
    }

    toggleSidebar(): void {
        this.setSidebarCollapsed(!this._isSidebarCollapsed());
    }

    setSidebarCollapsed(collapsed: boolean): void {
        this._isSidebarCollapsed.set(collapsed);
        this.applyBodyClass(collapsed);
        this.persistState(collapsed);
    }

    private applyBodyClass(collapsed: boolean): void {
        this.document.body.classList.toggle('mini-navbar', collapsed);
        this.document.documentElement.style.setProperty('--sidebar-width', collapsed ? '70px' : '220px');
    }

    private readInitialState(): boolean {
        if (!this.isBrowser()) {
            return false;
        }

        return localStorage.getItem(LayoutService.COLLAPSE_STORAGE_KEY) === 'on';
    }

    private persistState(collapsed: boolean): void {
        if (!this.isBrowser()) {
            return;
        }

        if (collapsed) {
            localStorage.setItem(LayoutService.COLLAPSE_STORAGE_KEY, 'on');
        } else {
            localStorage.removeItem(LayoutService.COLLAPSE_STORAGE_KEY);
        }
    }

    private isBrowser(): boolean {
        return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    }
}
